import { Fragment, useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { editarBreadcrumb } from '../../../actions/breadcrumb';
import {
    cargarAreasMovimientos,
    cargarOrganizadoresMovimientos,
    cargarUbicacionesMovimientos,
    listarProductosMovimientos,
    limpiarFormMovimientos,
    crearMovimientoInventario
} from '../../../actions/inventario';
import { setAlert } from '../../../actions/alert';

import { CustomValueContainer } from '../../../components/react_select/ReactSelect';

const styles_select = {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    container: (provided) => ({ ...provided }),
    valueContainer: (provided) => ({
        ...provided,
        overflow: 'visible',
        marginTop: '10px'
    }),
    placeholder: (provided, state) => ({
        ...provided,
        position: 'absolute',
        top: state.hasValue || state.selectProps.inputValue ? -15 : '0%',
        transition: 'top 0.1s, font-size 0.1s',
        fontSize: (state.hasValue || state.selectProps.inputValue) && 13
    })
};

const NuevoMovimiento = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const ROUTE_LISTADO = '/inventario/movimientos';
    const ROUTE_NUEVO = '/inventario/movimientos/nuevo';

    const {
        movimientosAreas,
        movimientosOrganizadoresOrigen,
        movimientosUbicacionesOrigen,
        movimientosOrganizadoresDestino,
        movimientosUbicacionesDestino,
        movimientosProductosOrigen
    } = useSelector(state => state.inventario || {});

    const [areaOrigen, setAreaOrigen] = useState(null);
    const [organizadorOrigen, setOrganizadorOrigen] = useState(null);
    const [ubicacionOrigen, setUbicacionOrigen] = useState(null);

    const [productoOrigen, setProductoOrigen] = useState(null);
    const [unidadOrigen, setUnidadOrigen] = useState(null);

    const [areaDestino, setAreaDestino] = useState(null);
    const [organizadorDestino, setOrganizadorDestino] = useState(null);
    const [ubicacionDestino, setUbicacionDestino] = useState(null);

    const [cantidadMover, setCantidadMover] = useState('');

    useEffect(() => {
        dispatch(editarBreadcrumb([
            { activo: false, nombre: 'Movimientos', path: ROUTE_LISTADO },
            { activo: true, nombre: 'Nuevo movimiento', path: ROUTE_NUEVO }
        ]));
    }, [dispatch]);

    useEffect(() => {
        dispatch(cargarAreasMovimientos());

        return () => {
            dispatch(limpiarFormMovimientos());
        };
    }, [dispatch]);

    const formatNum = (n) => {
        const num = Number(n || 0);
        return new Intl.NumberFormat('es-MX', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(num);
    };

    const areasOptions = useMemo(() => {
        const arr = Array.isArray(movimientosAreas) ? movimientosAreas : [];
        return arr
            .filter(a => !a?.deleted)
            .map(a => ({
                value: a?._id ?? null,
                label: a?.nombre || 'Sin definir',
                raw: a
            }));
    }, [movimientosAreas]);

    const organizadoresOrigenOptions = useMemo(() => {
        const arr = Array.isArray(movimientosOrganizadoresOrigen) ? movimientosOrganizadoresOrigen : [];
        return arr.map(o => ({
            value: o?._id ?? null,
            label: o?.nombre || 'Sin definir',
            raw: o
        }));
    }, [movimientosOrganizadoresOrigen]);

    const ubicacionesOrigenOptions = useMemo(() => {
        const arr = Array.isArray(movimientosUbicacionesOrigen) ? movimientosUbicacionesOrigen : [];
        return arr.map(u => ({
            value: u?._id ?? null,
            label: u?.nombre || 'Sin definir',
            raw: u
        }));
    }, [movimientosUbicacionesOrigen]);

    const organizadoresDestinoOptions = useMemo(() => {
        const arr = Array.isArray(movimientosOrganizadoresDestino) ? movimientosOrganizadoresDestino : [];
        return arr.map(o => ({
            value: o?._id ?? null,
            label: o?.nombre || 'Sin definir',
            raw: o
        }));
    }, [movimientosOrganizadoresDestino]);

    const ubicacionesDestinoOptions = useMemo(() => {
        const arr = Array.isArray(movimientosUbicacionesDestino) ? movimientosUbicacionesDestino : [];
        return arr.map(u => ({
            value: u?._id ?? null,
            label: u?.nombre || 'Sin definir',
            raw: u
        }));
    }, [movimientosUbicacionesDestino]);

    const productosOptions = useMemo(() => {
        const arr = Array.isArray(movimientosProductosOrigen) ? movimientosProductosOrigen : [];
        return arr.map(p => ({
            value: p?.producto?._id ?? null,
            label: p?.producto?.nombre || 'Sin definir',
            raw: p
        }));
    }, [movimientosProductosOrigen]);

    const unidadesOptions = useMemo(() => {
        const unidades = productoOrigen?.raw?.producto?.unidades;
        const arr = Array.isArray(unidades) ? unidades : [];
        return arr
            .map(u => ({
                value: String(u?.value ?? ''),
                label: u?.label || 'Sin definir',
                cantidad: Number(u?.cantidad || 1)
            }))
            .filter(u => u.value);
    }, [productoOrigen]);

    useEffect(() => {
        if (!productoOrigen) {
            setUnidadOrigen(null);
            return;
        }

        if (unidadesOptions.length > 0) {
            setUnidadOrigen(unidadesOptions[0]);
        } else {
            setUnidadOrigen(null);
        }
        setCantidadMover('');
    }, [productoOrigen, unidadesOptions]);

    const conversionActual = useMemo(() => {
        if (!unidadOrigen?.cantidad) return 1;
        const c = Number(unidadOrigen.cantidad || 1);
        return c > 0 ? c : 1;
    }, [unidadOrigen]);

    const cantidadBaseInventario = useMemo(() => {
        const base = Number(productoOrigen?.raw?.cantidad || 0);
        return base >= 0 ? base : 0;
    }, [productoOrigen]);

    const cantidadEnUnidad = useMemo(() => {
        if (!productoOrigen) return 0;
        return Number(cantidadBaseInventario || 0) * Number(conversionActual || 1);
    }, [productoOrigen, cantidadBaseInventario, conversionActual]);

    const maxMover = useMemo(() => {
        const v = Number(cantidadEnUnidad || 0);
        return v >= 0 ? v : 0;
    }, [cantidadEnUnidad]);

    const listarProductosOrigen = (nextArea, nextOrg, nextUbi) => {
        const id_area = nextArea?.value ?? null;
        const id_organizador = nextOrg?.value ?? null;
        const id_ubicacion = nextUbi?.value ?? null;

        if (!id_area) {
            dispatch(limpiarFormMovimientos({ keepAreas: true, keepDestino: true }));
            setProductoOrigen(null);
            setUnidadOrigen(null);
            setCantidadMover('');
            return;
        }

        dispatch(listarProductosMovimientos({
            id_area,
            id_organizador,
            id_ubicacion
        }));
        setProductoOrigen(null);
        setUnidadOrigen(null);
        setCantidadMover('');
    };

    const onChangeAreaOrigen = (opt) => {
        setAreaOrigen(opt || null);
        setOrganizadorOrigen(null);
        setUbicacionOrigen(null);

        const idArea = opt?.value ?? null;
        if (idArea) {
            dispatch(cargarOrganizadoresMovimientos(idArea, 'origen'));
        } else {
            dispatch(limpiarFormMovimientos({ keepAreas: true, keepDestino: true }));
        }

        listarProductosOrigen(opt || null, null, null);
    };

    const onChangeOrganizadorOrigen = (opt) => {
        setOrganizadorOrigen(opt || null);
        setUbicacionOrigen(null);

        const idOrg = opt?.value ?? null;
        if (idOrg) {
            dispatch(cargarUbicacionesMovimientos(idOrg, 'origen'));
        } else {
            dispatch(cargarUbicacionesMovimientos(null, 'origen'));
        }

        listarProductosOrigen(areaOrigen, opt || null, null);
    };

    const onChangeUbicacionOrigen = (opt) => {
        setUbicacionOrigen(opt || null);
        listarProductosOrigen(areaOrigen, organizadorOrigen, opt || null);
    };

    const onChangeProductoOrigen = (opt) => {
        setProductoOrigen(opt || null);
        setCantidadMover('');
    };

    const onChangeUnidadOrigen = (opt) => {
        setUnidadOrigen(opt || null);
        setCantidadMover('');
    };

    const onChangeAreaDestino = (opt) => {
        setAreaDestino(opt || null);
        setOrganizadorDestino(null);
        setUbicacionDestino(null);

        const idArea = opt?.value ?? null;
        if (idArea) {
            dispatch(cargarOrganizadoresMovimientos(idArea, 'destino'));
        } else {
            dispatch(cargarOrganizadoresMovimientos(null, 'destino'));
            dispatch(cargarUbicacionesMovimientos(null, 'destino'));
        }
    };

    const onChangeOrganizadorDestino = (opt) => {
        setOrganizadorDestino(opt || null);
        setUbicacionDestino(null);

        const idOrg = opt?.value ?? null;
        if (idOrg) {
            dispatch(cargarUbicacionesMovimientos(idOrg, 'destino'));
        } else {
            dispatch(cargarUbicacionesMovimientos(null, 'destino'));
        }
    };

    const onChangeUbicacionDestino = (opt) => {
        setUbicacionDestino(opt || null);
    };

    const clampCantidadMover = (val) => {
        const cleaned = String(val ?? '').replace(',', '.');
        if (cleaned === '') return '';

        let num = Number(cleaned);
        if (!Number.isFinite(num)) return '';

        if (num < 1) num = 1;
        if (num > maxMover) num = maxMover;

        return String(num);
    };

    const onChangeCantidadMover = (e) => {
        setCantidadMover(clampCantidadMover(e.target.value));
    };

    const resetForm = () => {
        setAreaOrigen(null);
        setOrganizadorOrigen(null);
        setUbicacionOrigen(null);
        setProductoOrigen(null);
        setUnidadOrigen(null);
        setCantidadMover('');
        setAreaDestino(null);
        setOrganizadorDestino(null);
        setUbicacionDestino(null);
        dispatch(limpiarFormMovimientos({ keepAreas: true }));
    };

    const onGuardar = async () => {
        if (!areaOrigen?.value) {
            dispatch(setAlert('Selecciona un área de origen.', 'danger'));
            return;
        }

        if (!productoOrigen?.raw?.producto?._id) {
            dispatch(setAlert('Selecciona un producto en el origen.', 'danger'));
            return;
        }

        if (!areaDestino?.value) {
            dispatch(setAlert('Selecciona un área de destino.', 'danger'));
            return;
        }

        const mover = Number(cantidadMover || 0);
        if (!Number.isFinite(mover) || mover < 1) {
            dispatch(setAlert('La cantidad a mover debe ser mínimo 1.', 'danger'));
            return;
        }

        if (mover > maxMover) {
            dispatch(setAlert('La cantidad a mover no puede ser mayor a lo disponible.', 'danger'));
            return;
        }

        const id_producto = productoOrigen.raw.producto._id;
        const id_unidad = unidadOrigen?.value ?? null;
        const conversion = id_unidad ? Number(unidadOrigen?.cantidad || 1) : 1;
        const cantidad = conversion > 0 ? (mover / conversion) : mover;

        const payload = {
            id_producto,
            cantidad,
            conversion: conversion || 1,
            id_unidad,
            id_area_origen: areaOrigen?.value ?? null,
            id_organizador_origen: organizadorOrigen?.value ?? null,
            id_ubicacion_origen: ubicacionOrigen?.value ?? null,
            id_area_destino: areaDestino?.value ?? null,
            id_organizador_destino: organizadorDestino?.value ?? null,
            id_ubicacion_destino: ubicacionDestino?.value ?? null
        };

        const ok = await dispatch(crearMovimientoInventario(payload));
        if (ok) {
            navigate(ROUTE_LISTADO);
        }
    };

    return (
        <Fragment>
            <div className="page-wrapper">
                <div className="page-body">
                    <div className="card">
                        <div className="card-body">
                            <h3 className="mb-4">Nuevo movimiento</h3>

                            <h5 className="mb-3 text-success">Origen</h5>

                            <div className="row">
                                <div className="col-12 col-md-4 mb-3">
                                    <div className="input-group h-100">
                                        <Select
                                            options={areasOptions}
                                            value={areaOrigen}
                                            onChange={onChangeAreaOrigen}
                                            isClearable
                                            placeholder="Área"
                                            noOptionsMessage={() => 'Sin resultados'}
                                            menuPortalTarget={document.body}
                                            menuPlacement="auto"
                                            components={{ ValueContainer: CustomValueContainer }}
                                            styles={{ ...styles_select }}
                                            className="select-group w-100"
                                            classNamePrefix="react-select"
                                        />
                                    </div>
                                </div>

                                <div className="col-12 col-md-4 mb-3">
                                    <div className="input-group h-100">
                                        <Select
                                            options={organizadoresOrigenOptions}
                                            value={organizadorOrigen}
                                            onChange={onChangeOrganizadorOrigen}
                                            isClearable
                                            isDisabled={!areaOrigen?.value || organizadoresOrigenOptions.length === 0}
                                            placeholder="Organizador"
                                            noOptionsMessage={() => 'Sin resultados'}
                                            menuPortalTarget={document.body}
                                            menuPlacement="auto"
                                            components={{ ValueContainer: CustomValueContainer }}
                                            styles={{ ...styles_select }}
                                            className="select-group w-100"
                                            classNamePrefix="react-select"
                                        />
                                    </div>
                                </div>

                                <div className="col-12 col-md-4 mb-3">
                                    <div className="input-group h-100">
                                        <Select
                                            options={ubicacionesOrigenOptions}
                                            value={ubicacionOrigen}
                                            onChange={onChangeUbicacionOrigen}
                                            isClearable
                                            isDisabled={!areaOrigen?.value || !organizadorOrigen?.value || ubicacionesOrigenOptions.length === 0}
                                            placeholder="Ubicación"
                                            noOptionsMessage={() => 'Sin resultados'}
                                            menuPortalTarget={document.body}
                                            menuPlacement="auto"
                                            components={{ ValueContainer: CustomValueContainer }}
                                            styles={{ ...styles_select }}
                                            className="select-group w-100"
                                            classNamePrefix="react-select"
                                        />
                                    </div>
                                </div>

                                <div className="col-12 col-md-6 mb-3">
                                    <div className="input-group h-100">
                                        <Select
                                            options={productosOptions}
                                            value={productoOrigen}
                                            onChange={onChangeProductoOrigen}
                                            isClearable
                                            isDisabled={!areaOrigen?.value}
                                            placeholder="Producto"
                                            noOptionsMessage={() => 'Sin resultados'}
                                            menuPortalTarget={document.body}
                                            menuPlacement="auto"
                                            components={{ ValueContainer: CustomValueContainer }}
                                            styles={{ ...styles_select }}
                                            className="select-group w-100"
                                            classNamePrefix="react-select"
                                        />
                                    </div>
                                </div>

                                <div className="col-12 col-md-3 mb-3">
                                    <div className="input-group h-100">
                                        <Select
                                            options={unidadesOptions}
                                            value={unidadOrigen}
                                            onChange={onChangeUnidadOrigen}
                                            isClearable
                                            isDisabled={!productoOrigen || unidadesOptions.length === 0}
                                            placeholder="Unidad"
                                            noOptionsMessage={() => 'Sin resultados'}
                                            menuPortalTarget={document.body}
                                            menuPlacement="auto"
                                            components={{ ValueContainer: CustomValueContainer }}
                                            styles={{ ...styles_select }}
                                            className="select-group w-100"
                                            classNamePrefix="react-select"
                                        />
                                    </div>
                                </div>

                                <div className="col-12 col-md-3 mb-3">
                                    <div className="form-floating">
                                        <input
                                            type="text"
                                            className="form-control"
                                            style={{ height: '58px' }}
                                            placeholder="Cantidad en inventario"
                                            id="cantidad_inventario"
                                            value={productoOrigen ? `${formatNum(cantidadEnUnidad)}` : ''}
                                            disabled
                                        />
                                        <label htmlFor="cantidad_inventario">Cantidad en inventario</label>
                                    </div>
                                </div>
                            </div>

                            <hr className="my-4" />

                            <h5 className="mb-3 text-success">Destino</h5>

                            <div className="row">
                                <div className="col-12 col-md-3 mb-3">
                                    <div className="input-group h-100">
                                        <Select
                                            options={areasOptions}
                                            value={areaDestino}
                                            onChange={onChangeAreaDestino}
                                            isClearable
                                            placeholder="Área"
                                            noOptionsMessage={() => 'Sin resultados'}
                                            menuPortalTarget={document.body}
                                            menuPlacement="auto"
                                            components={{ ValueContainer: CustomValueContainer }}
                                            styles={{ ...styles_select }}
                                            className="select-group w-100"
                                            classNamePrefix="react-select"
                                        />
                                    </div>
                                </div>

                                <div className="col-12 col-md-3 mb-3">
                                    <div className="input-group h-100">
                                        <Select
                                            options={organizadoresDestinoOptions}
                                            value={organizadorDestino}
                                            onChange={onChangeOrganizadorDestino}
                                            isClearable
                                            isDisabled={!areaDestino?.value || organizadoresDestinoOptions.length === 0}
                                            placeholder="Organizador"
                                            noOptionsMessage={() => 'Sin resultados'}
                                            menuPortalTarget={document.body}
                                            menuPlacement="auto"
                                            components={{ ValueContainer: CustomValueContainer }}
                                            styles={{ ...styles_select }}
                                            className="select-group w-100"
                                            classNamePrefix="react-select"
                                        />
                                    </div>
                                </div>

                                <div className="col-12 col-md-3 mb-3">
                                    <div className="input-group h-100">
                                        <Select
                                            options={ubicacionesDestinoOptions}
                                            value={ubicacionDestino}
                                            onChange={onChangeUbicacionDestino}
                                            isClearable
                                            isDisabled={!areaDestino?.value || !organizadorDestino?.value || ubicacionesDestinoOptions.length === 0}
                                            placeholder="Ubicación"
                                            noOptionsMessage={() => 'Sin resultados'}
                                            menuPortalTarget={document.body}
                                            menuPlacement="auto"
                                            components={{ ValueContainer: CustomValueContainer }}
                                            styles={{ ...styles_select }}
                                            className="select-group w-100"
                                            classNamePrefix="react-select"
                                        />
                                    </div>
                                </div>

                                <div className="col-12 col-md-3 mb-3">
                                    <div className="form-floating">
                                        <input
                                            type="number"
                                            className="form-control"
                                            style={{ height: '58px' }}
                                            placeholder="Cantidad a mover"
                                            id="cantidad_mover"
                                            min="1"
                                            step="any"
                                            disabled={!productoOrigen || maxMover <= 0}
                                            value={cantidadMover}
                                            onChange={onChangeCantidadMover}
                                        />
                                        <label htmlFor="cantidad_mover">Cantidad a mover</label>
                                    </div>
                                </div>
                            </div>

                            <div className="d-flex justify-content-end gap-2 mt-4">
                                <button
                                    type="button"
                                    className="btn btn-danger"
                                    onClick={resetForm}
                                >
                                    Cancelar
                                </button>

                                <button
                                    type="button"
                                    className="btn btn-success"
                                    onClick={onGuardar}
                                >
                                    Guardar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default NuevoMovimiento;